import {
  Button,
  DialogActionTrigger,
  DialogBody,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  HStack,
  Switch,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { CenteredDialogContent } from '#components/ui/centered-dialog-content';
import { toaster } from '#components/ui/toaster';

import { uploadFile } from '../upload-image';

export interface DocumentAttachFileModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  apiHost: string;
  userEmail: string;
  documentId: string;
  /** Called after a successful upload (e.g. refresh a list). */
  onUploaded?: () => void;
}

export function DocumentAttachFileModal({
  open,
  onOpenChange,
  apiHost,
  userEmail,
  documentId,
  onUploaded,
}: DocumentAttachFileModalProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [lastViewUrl, setLastViewUrl] = useState<string | null>(null);
  const [encryptUpload, setEncryptUpload] = useState(false);

  useEffect(() => {
    if (!open) return;
    setFile(null);
    setLastViewUrl(null);
    setUploading(false);
    setEncryptUpload(false);
    if (inputRef.current) inputRef.current.value = '';
  }, [open]);

  const handleUpload = useCallback(async () => {
    if (!file) return;
    setUploading(true);
    setLastViewUrl(null);
    try {
      const result = await uploadFile(file, {
        apiHost,
        userEmail,
        documentId,
        encrypt: encryptUpload,
      });
      setLastViewUrl(`${apiHost}/api/upload/${result.id}`);
      toaster.create({
        title: 'File uploaded',
        description: 'The file is linked to this document.',
        type: 'success',
      });
      onUploaded?.();
    } catch (error) {
      toaster.create({
        title: 'Upload failed',
        description:
          error instanceof Error ? error.message : 'Could not upload file',
        type: 'error',
      });
    } finally {
      setUploading(false);
    }
  }, [apiHost, documentId, encryptUpload, file, onUploaded, userEmail]);

  const handleCopyLink = useCallback(() => {
    if (!lastViewUrl) return;
    void navigator.clipboard
      .writeText(lastViewUrl)
      .then(() => {
        toaster.create({
          title: 'Copied',
          description: 'View URL copied to clipboard',
          type: 'success',
        });
      })
      .catch(() => {
        toaster.create({
          title: 'Copy failed',
          description: 'Could not copy to clipboard',
          type: 'error',
        });
      });
  }, [lastViewUrl]);

  return (
    <DialogRoot
      open={open}
      onOpenChange={(e) => {
        if (!e.open) onOpenChange(false);
      }}
    >
      <CenteredDialogContent maxW="min(90vw, 28rem)">
        <DialogHeader>
          <DialogTitle>Upload file</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <VStack gap={3} align="stretch">
            <Text fontSize="sm" color="fg.muted">
              The file is stored in your uploads and associated with this
              document. You can open it from the Uploads page or use the link
              below.
            </Text>
            <HStack align="center" gap={2}>
              <Switch.Root
                checked={encryptUpload}
                onCheckedChange={(e) => setEncryptUpload(Boolean(e.checked))}
                disabled={uploading}
              >
                <Switch.HiddenInput />
                <Switch.Control>
                  <Switch.Thumb />
                </Switch.Control>
              </Switch.Root>
              <Text fontSize="sm" color="fg.muted">
                Encrypt upload (at-rest in your bucket)
              </Text>
            </HStack>
            <input
              ref={inputRef}
              type="file"
              style={{ display: 'none' }}
              onChange={(e) => {
                const f = e.target.files?.[0] ?? null;
                setFile(f);
                setLastViewUrl(null);
              }}
            />
            <Button
              variant="outline"
              size="sm"
              alignSelf="flex-start"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
            >
              {file ? 'Choose a different file' : 'Choose file'}
            </Button>
            {file ? (
              <Text fontSize="sm" wordBreak="break-all">
                Selected: {file.name}
              </Text>
            ) : null}
            {lastViewUrl ? (
              <Text fontSize="xs" fontFamily="mono" color="fg.muted">
                {lastViewUrl}
              </Text>
            ) : null}
          </VStack>
        </DialogBody>
        <DialogFooter>
          <HStack gap={2} flexWrap="wrap">
            <DialogActionTrigger asChild>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Close
              </Button>
            </DialogActionTrigger>
            {lastViewUrl ? (
              <Button size="sm" variant="outline" onClick={handleCopyLink}>
                Copy link
              </Button>
            ) : null}
            <Button
              colorPalette="blue"
              loading={uploading}
              disabled={!file || uploading}
              onClick={() => void handleUpload()}
            >
              Upload
            </Button>
          </HStack>
        </DialogFooter>
      </CenteredDialogContent>
    </DialogRoot>
  );
}
